import React, { useRef } from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

export interface IBreakdownTab {
  key: string;
  label: string;
}

export interface IBreakdownTabsProps {
  items: IBreakdownTab[];
  activeKey: string;
  onChange: (key: string) => void;
  className?: string;
}

/** The id a breakdown body points its own `aria-labelledby` at. */
export const breakdownTabId = (key: string): string => `breakdown-tab-${key}`;

const ACTIVE_TINT_OPACITY = 0.18;

const TabList = styled.div`
  display: flex;
  flex-wrap: wrap;

  /* Flex gap needs Safari 14.1 (cross-browser.md section 5). */
  > * + * {
    margin-left: ${tokens.space['8']};
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  position: relative;
  padding: ${tokens.space['8']} ${tokens.space['12']};
  border: 1px solid
    ${({ $active }) => ($active ? tokens.color['accent-purple'] : tokens.color['border-subtle'])};
  border-radius: ${tokens.radius.sm};
  background: ${tokens.color['surface-section']};
  color: ${({ $active }) => ($active ? tokens.color['accent-blue'] : tokens.color['text-primary'])};
  cursor: pointer;
  font-size: ${tokens.type['body-size']};
  font-weight: ${({ $active }) =>
    $active ? tokens.type['card-title-weight'] : tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};

  /* The selected tab carries an 18% wash of the family blue. A token is a CSS custom-property
     reference, so the alpha rides its own layer instead of the value (ConfidenceBadge precedent). */
  ${({ $active }) =>
    $active
      ? `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: inherit;
      background: ${tokens.color['accent-blue']};
      opacity: ${ACTIVE_TINT_OPACITY};
      pointer-events: none;
    }
  `
      : ''}

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

const TabLabel = styled.span`
  position: relative;
`;

const nextPosition = (key: string, position: number, count: number): number => {
  switch (key) {
    case 'ArrowRight':
      return (position + 1) % count;
    case 'ArrowLeft':
      return (position - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return -1;
  }
};

const BreakdownTabs: React.FC<IBreakdownTabsProps> = ({
  items,
  activeKey,
  onChange,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  const tabRefs = useRef<HTMLButtonElement[]>([]);
  const tabs = Array.isArray(items) ? items : [];
  const activePosition = tabs.findIndex(tab => tab.key === activeKey);

  if (tabs.length === 0) {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, position: number) => {
    const target = nextPosition(event.key, position, tabs.length);

    if (target === -1) {
      return;
    }

    event.preventDefault();
    onChange(tabs[target].key);
    tabRefs.current[target]?.focus();
  };

  return (
    <TabList
      className={className}
      role="tablist"
      aria-label={t(`${TCustomerInsights}.Breakdowns.TabsLabel`)}
      data-testid="breakdown-tabs"
    >
      {tabs.map((tab, position) => {
        const isActive = position === activePosition;

        return (
          <Tab
            key={tab.key}
            ref={element => {
              tabRefs.current[position] = element;
            }}
            id={breakdownTabId(tab.key)}
            type="button"
            role="tab"
            $active={isActive}
            aria-selected={isActive}
            // An activeKey matching no tab would otherwise leave the whole strip out of the tab
            // order, so the first tab keeps the roving stop.
            tabIndex={isActive || (activePosition === -1 && position === 0) ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={event => handleKeyDown(event, position)}
            data-testid="breakdown-tabs-tab"
          >
            <TabLabel>{tab.label}</TabLabel>
          </Tab>
        );
      })}
    </TabList>
  );
};

export default BreakdownTabs;
